import { Component, Host, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'breadcrumbs-type-1';

/**
 * Breadcrumb navigation component for SignNow brand.
 * Renders an ordered list of navigation links with configurable separators
 * and schema.org BreadcrumbList microdata support.
 *
 * @product signnow
 * @source breadcrumb
 * @version 2.0.0
 * @category SignNow
 * @contentConfig Breadcrumb navigation displaying a path from root to current page
 * @example
 *   <sn-breadcrumbs-type-1 items='[{"label": "Home", "href": "/"}, {"label": "Features", "href": "/features"}, {"label": "eSignature"}]'></sn-breadcrumbs-type-1>
 */
@Component({
  tag: 'sn-breadcrumbs-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNBreadcrumbsType1 {
  /**
   * Breadcrumb navigation items displayed in order from root to current page.
   * @content
   * @type {array}
   * @items.type {object}
   * @items.property {string} label - Visible breadcrumb text (e.g. "Home", "Features")
   * @items.property {string} href - Navigation URL. Omit for the last (current page) item to render it as plain text.
   * @items.required label
   * @items.content label
   * @minItems 1
   */
  @Prop({ mutable: true }) items?: any[] | string;

  /**
   * Icon name for separator between breadcrumb items.
   * @default "arrows--chevron-right"
   */
  @Prop() separator: string = 'arrows--chevron-right';

  /**
   * Color mode for the component.
   * @default "signnow"
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Add schema.org BreadcrumbList microdata markup.
   * @default true
   */
  @Prop() withSchemaOrgEntities: boolean = true;

  private parsedItems: any[] = [];

  componentWillLoad() {
    const parsed = parseJsonProp(this.items, []);
    this.parsedItems = Array.isArray(parsed) ? parsed : [];
  }

  render() {
    const items = this.parsedItems;
    if (!items || items.length === 0) return null;

    return (
      <Host data-color-mode={this.colorMode}>
        <me-section yGap="sm" yGapLg="sm">
          <nav
            class={COMPONENT_NAME}
            aria-label="Breadcrumb"
            role="navigation"
            {...(this.withSchemaOrgEntities
              ? { itemscope: true, itemtype: 'https://schema.org/BreadcrumbList' }
              : {})}
          >
            <ol class={`${COMPONENT_NAME}__list`}>
              {items.map((item, index) => {
                const isLast = index === items.length - 1;
                return (
                  <li
                    class={`${COMPONENT_NAME}__item${isLast ? ` ${COMPONENT_NAME}__item--current` : ''}`}
                    {...(this.withSchemaOrgEntities
                      ? { itemscope: true, itemtype: 'https://schema.org/ListItem', itemprop: 'itemListElement' }
                      : {})}
                  >
                    {item.href && !isLast ? (
                      <a
                        class={`${COMPONENT_NAME}__link`}
                        href={item.href}
                        aria-label={item.label}
                        {...(this.withSchemaOrgEntities ? { itemprop: 'item' } : {})}
                      >
                        <span {...(this.withSchemaOrgEntities ? { itemprop: 'name' } : {})}>{item.label}</span>
                      </a>
                    ) : (
                      <span
                        class={`${COMPONENT_NAME}__text`}
                        aria-current={isLast ? 'page' : undefined}
                        {...(this.withSchemaOrgEntities ? { itemprop: 'name' } : {})}
                      >
                        {item.label}
                      </span>
                    )}
                    {this.withSchemaOrgEntities && isLast && item.href && (
                      <meta itemprop="item" content={item.href} />
                    )}
                    {!isLast && (
                      <span class={`${COMPONENT_NAME}__separator`} aria-hidden="true">
                        <me-icon name={this.separator} inline />
                      </span>
                    )}
                    {this.withSchemaOrgEntities && (
                      <meta itemprop="position" content={String(index + 1)} />
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        </me-section>
      </Host>
    );
  }
}
